import React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { Alert, Button, Form } from 'react-bootstrap';
import StarRating from './StarRating';

class ProductReviews extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      reviews: [],
      averageRating: 0,
      reviewCount: 0,
      loading: true,
      loadError: false,
      authorName: '',
      rating: 0,
      comment: '',
      submitting: false,
      submitError: '',
      submitSuccess: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleRatingChange = this.handleRatingChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    this.loadReviews();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.productId !== this.props.productId) {
      this.loadReviews();
    }
  }

  async loadReviews() {
    const { productId } = this.props;
    if (!productId) return;

    this.setState({ loading: true, loadError: false });
    try {
      const { data } = await axios.get(`/api/products/${productId}/reviews`);
      this.setState({
        reviews: data.reviews || [],
        averageRating: data.averageRating || 0,
        reviewCount: data.reviewCount || 0,
        loading: false,
      });
      if (this.props.onSummaryChange) {
        this.props.onSummaryChange({
          averageRating: data.averageRating || 0,
          reviewCount: data.reviewCount || 0,
        });
      }
    } catch (err) {
      console.error(err);
      this.setState({ loading: false, loadError: true });
    }
  }

  handleChange(evt) {
    this.setState({ [evt.target.name]: evt.target.value, submitError: '' });
  }

  handleRatingChange(rating) {
    this.setState({ rating, submitError: '' });
  }

  async handleSubmit(evt) {
    evt.preventDefault();
    if (this.state.submitting) return;

    const { authorName, rating, comment } = this.state;
    if (!authorName.trim()) {
      this.setState({ submitError: 'Please enter your name.' });
      return;
    }
    if (!rating) {
      this.setState({ submitError: 'Please choose a star rating.' });
      return;
    }

    this.setState({ submitting: true, submitError: '', submitSuccess: false });
    try {
      const { data } = await axios.post(
        `/api/products/${this.props.productId}/reviews`,
        {
          authorName: authorName.trim(),
          rating,
          comment: comment.trim(),
        }
      );
      const reviews = [data, ...this.state.reviews];
      const reviewCount = reviews.length;
      const total = reviews.reduce((sum, review) => sum + review.rating, 0);
      const averageRating = Math.round((total / reviewCount) * 10) / 10;

      this.setState({
        reviews,
        averageRating,
        reviewCount,
        authorName: '',
        rating: 0,
        comment: '',
        submitting: false,
        submitSuccess: true,
      });
      if (this.props.onSummaryChange) {
        this.props.onSummaryChange({ averageRating, reviewCount });
      }
    } catch (err) {
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        'Could not submit your review. Please try again.';
      this.setState({ submitting: false, submitError: message });
    }
  }

  formatDate(value) {
    try {
      return new Date(value).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (err) {
      return '';
    }
  }

  render() {
    const {
      reviews,
      averageRating,
      reviewCount,
      loading,
      loadError,
      authorName,
      rating,
      comment,
      submitting,
      submitError,
      submitSuccess,
    } = this.state;

    return (
      <section className="product-reviews-block marg">
        <h2 className="product-details-title">Reviews</h2>

        <div className="product-reviews-summary">
          <StarRating value={averageRating} size="lg" />
          <p className="product-reviews-summary-text">
            {reviewCount === 0
              ? 'No reviews yet. Be the first to share your experience.'
              : `${averageRating.toFixed(1)} average · ${reviewCount} review${
                  reviewCount === 1 ? '' : 's'
                }`}
          </p>
        </div>

        {loadError && (
          <Alert variant="danger">Could not load reviews right now.</Alert>
        )}

        {!loading && !loadError && reviews.length > 0 && (
          <ul className="product-reviews-list">
            {reviews.map((review) => (
              <li key={review.id} className="product-review-item">
                <div className="product-review-meta">
                  <StarRating value={review.rating} size="sm" />
                  <span className="product-review-author">{review.authorName}</span>
                  <span className="product-review-date">
                    {this.formatDate(review.createdAt)}
                  </span>
                </div>
                {review.comment && (
                  <p className="product-review-comment">{review.comment}</p>
                )}
              </li>
            ))}
          </ul>
        )}

        <Form className="product-review-form" onSubmit={this.handleSubmit}>
          <h3 className="product-review-form-title">Write a review</h3>
          <Form.Group className="mb-3">
            <Form.Label>Your rating</Form.Label>
            <StarRating
              value={rating}
              onChange={this.handleRatingChange}
              interactive
              size="lg"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              name="authorName"
              value={authorName}
              onChange={this.handleChange}
              maxLength={80}
              placeholder="Your name"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Review (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="comment"
              value={comment}
              onChange={this.handleChange}
              maxLength={2000}
              placeholder="What did you think?"
            />
          </Form.Group>
          {submitError && <Alert variant="danger">{submitError}</Alert>}
          {submitSuccess && (
            <Alert variant="success">Thank you — your review was posted.</Alert>
          )}
          <Button type="submit" variant="success" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit review'}
          </Button>
        </Form>
      </section>
    );
  }
}

ProductReviews.propTypes = {
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSummaryChange: PropTypes.func,
};

export default ProductReviews;
